import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  input,
  output,
  HostBinding,
  computed,
  inject,
} from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { LucideX } from '@lucide/angular';
import { DialogBase } from '../shared/dialog-base';

export type DrawerPosition = 'left' | 'right' | 'top' | 'bottom';
export type DrawerMode = 'push' | 'over';

let nextDrawerId = 0;

/**
 * Directional slide-in drawer with overlay or push modes.
 * Rendered via portal to document.body to escape parent stacking contexts.
 *
 * ## Modes
 * - **`over`** (default) — drawer slides over page content
 * - **`push`** — drawer pushes the host element and its siblings
 *
 * ## Usage
 * ```html
 * <ds-drawer [open]="isOpen()" position="right" mode="over" title="Title" (closed)="isOpen.set(false)">
 *   drawer content here
 * </ds-drawer>
 *
 * <!-- Full-width right drawer -->
 * <ds-drawer [open]="isOpen()" position="right" [fullWidth]="true" (closed)="isOpen.set(false)">
 *   ...
 * </ds-drawer>
 * ```
 */
@Component({
  selector: 'ds-drawer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet],
  template: `
    @if (open()) {
      <div
        class="drawer-overlay"
        [class.drawer-overlay--transparent]="mode() === 'push'"
        (click)="handleBackdrop()"
      >
        <div
          #dialog
          class="drawer"
          [class.drawer--left]="position() === 'left'"
          [class.drawer--right]="position() === 'right'"
          [class.drawer--top]="position() === 'top'"
          [class.drawer--bottom]="position() === 'bottom'"
          [class.drawer--full-width]="fullWidth()"
          [style.width]="drawerSize"
          role="dialog"
          aria-modal="true"
          tabindex="-1"
          [attr.aria-labelledby]="drawerId + '-title'"
          (click)="$event.stopPropagation()"
          (keydown)="handleKeydown($event)"
        >
          @if (title()) {
            <header class="drawer__header">
              <h2 class="drawer__title" [id]="drawerId + '-title'">{{ title() }}</h2>
              @if (dismissible()) {
                <button
                  type="button"
                  class="drawer__close"
                  [attr.aria-label]="closeLabel()"
                  (click)="closed.emit()"
                >
                  <ng-container
                    [ngComponentOutlet]="closeIcon"
                    [ngComponentOutletInputs]="{ size: 22, strokeWidth: 2.5 }"
                  />
                </button>
              }
            </header>
          }
          <div class="drawer__body">
            <ng-content />
          </div>
        </div>
      </div>
    }
  `,
  styles: `
    :host {
      display: contents;
    }

    /* Push mode: shift the host element when drawer is open */
    :host.drawer-host--push-left {
      transform: translateX(v-bind(pushOffset));
      transition: transform var(--motion-base);
    }
    :host.drawer-host--push-right {
      transform: translateX(calc(-1 * v-bind(pushOffset)));
      transition: transform var(--motion-base);
    }
    :host.drawer-host--push-top {
      transform: translateY(v-bind(pushOffset));
      transition: transform var(--motion-base);
    }
    :host.drawer-host--push-bottom {
      transform: translateY(calc(-1 * v-bind(pushOffset)));
      transition: transform var(--motion-base);
    }

    .drawer-overlay {
      position: fixed;
      inset: 0;
      z-index: 2147483647;
      display: flex;
      background: rgba(32, 37, 31, 0.55);
      animation: overlay-in var(--motion-base);

      &--transparent {
        background: transparent;
        pointer-events: none;
      }
    }

    .drawer {
      display: flex;
      flex-direction: column;
      background: var(--color-surface);
      box-shadow: var(--shadow-raised);
      overflow: hidden;
      animation: drawer-in var(--motion-base);
    }

    /* Left/right drawers: width set via inline style, full viewport height */
    .drawer--left,
    .drawer--right {
      height: 100%;
      min-width: 320px;
      max-width: 100%;
    }

    /* Top/bottom drawers: height set via inline style, full viewport width */
    .drawer--top,
    .drawer--bottom {
      width: 100%;
      min-height: 200px;
      max-height: 100%;
    }

    /* Full-width variant */
    .drawer--full-width.drawer--left,
    .drawer--full-width.drawer--right {
      width: 100%;
      max-width: 100%;
    }
    .drawer--full-width.drawer--top,
    .drawer--full-width.drawer--bottom {
      height: 100%;
      max-height: 100%;
    }

    /* Left drawer: anchor to left edge, rounded on the right (inner) edge */
    .drawer--left {
      align-self: flex-start;
      border-right: 1px solid var(--color-border);
      border-radius: 0 var(--radius-lg) var(--radius-lg) 0;
    }

    /* Right drawer: anchor to right edge, rounded on the left (inner) edge */
    .drawer--right {
      align-self: flex-end;
      margin-left: auto;
      border-left: 1px solid var(--color-border);
      border-radius: var(--radius-lg) 0 0 var(--radius-lg);
    }

    /* Top drawer: anchor to top edge, rounded on the bottom (inner) edge */
    .drawer--top {
      align-self: flex-start;
      border-bottom: 1px solid var(--color-border);
      border-radius: 0 0 var(--radius-lg) var(--radius-lg);
    }

    /* Bottom drawer: anchor to bottom edge, rounded on the top (inner) edge */
    .drawer--bottom {
      align-self: flex-end;
      margin-top: auto;
      border-top: 1px solid var(--color-border);
      border-radius: var(--radius-lg) var(--radius-lg) 0 0;
    }

    .drawer__header {
      display: flex;
      align-items: center;
      gap: var(--space-3);
      padding: var(--space-4) var(--space-4) var(--space-3);
      flex-shrink: 0;
    }

    .drawer__title {
      flex: 1;
      margin: 0;
      font-size: var(--text-lg);
      font-weight: var(--font-weight-semibold);
    }

    .drawer__close {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: var(--touch-target);
      height: var(--touch-target);
      margin-right: calc(var(--space-2) * -1);
      border: 0;
      border-radius: var(--radius-full);
      background: transparent;
      color: var(--color-text-secondary);
      cursor: pointer;
      transition: background-color var(--motion-fast);

      &:hover {
        background: var(--color-bg-subtle);
        color: var(--color-text);
      }
    }

    .drawer__body {
      flex: 1;
      overflow-y: auto;
      padding: 0 var(--space-4) var(--space-4);
    }

    @keyframes overlay-in {
      from { opacity: 0; }
    }

    @keyframes drawer-in-left  { from { transform: translateX(-100%); } }
    @keyframes drawer-in-right { from { transform: translateX(100%); } }
    @keyframes drawer-in-top   { from { transform: translateY(-100%); } }
    @keyframes drawer-in-bottom{ from { transform: translateY(100%); } }

    .drawer--left   { animation-name: drawer-in-left; }
    .drawer--right  { animation-name: drawer-in-right; }
    .drawer--top    { animation-name: drawer-in-top; }
    .drawer--bottom { animation-name: drawer-in-bottom; }
  `,
})
export class DrawerComponent extends DialogBase implements AfterViewInit {
  private readonly hostEl = inject(ElementRef<HTMLElement>);

  /** Edge from which the drawer slides. */
  position = input<DrawerPosition>('right');

  /** `over` renders above content; `push` shifts the host element. */
  mode = input<DrawerMode>('over');

  /**
   * Width for left/right drawers, height for top/bottom drawers.
   * Ignored when `fullWidth` is true.
   */
  size = input<string>('320px');

  /** When true, drawer spans the full viewport width (or height for top/bottom). */
  fullWidth = input(false);

  /** Header title. Shows header with close button when provided. */
  title = input<string>('');

  /** Allow dismissal by clicking the overlay backdrop. */
  dismissible = input(true);

  closeLabel = input('Tutup panel');
  closed = output<void>();

  protected readonly drawerId = `ds-drawer-${nextDrawerId++}`;
  protected readonly closeIcon = LucideX;

  /** CSS size value — respects fullWidth override. */
  protected readonly drawerSize = computed(() =>
    this.fullWidth() ? '100%' : this.size(),
  );

  /** CSS offset used for push mode host transform. */
  protected readonly pushOffset = computed(() =>
    this.fullWidth() ? '100%' : this.size(),
  );

  /** CSS class applied to the host element for push mode transforms. */
  @HostBinding('class') get hostClass(): string {
    if (this.mode() !== 'push' || !this.open()) return '';
    return `drawer-host--push drawer-host--push-${this.position()}`;
  }

  ngAfterViewInit(): void {
    // Portal the host element to document.body so the drawer escapes
    // any parent stacking context (e.g., admin shell's overflow:hidden on main).
    document.body.appendChild(this.hostEl.nativeElement);
  }

  protected handleBackdrop(): void {
    if (this.dismissible()) this.closed.emit();
  }

  protected requestClose(): void {
    this.closed.emit();
  }
}
